// src/App.tsx
// Change #12 - 2025-01-15 10:50:00Z: Complete route structure fix
import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import ChannelSettings from '@/pages/Settings'

// Change #12: Fixed import and added lazy loading
const YouTubeAuthSuccess = React.lazy(() => import('@/pages/YouTubeAuthSuccess'))
// Layout Components
import MainLayout from '@/components/layout/MainLayout';

// Auth Pages
import { SignIn } from '@/pages/auth/SignIn';
import { SignUp } from '@/pages/auth/SignUp';
import { ForgotPassword } from '@/pages/auth/ForgotPassword';

const VideoSettings = React.lazy(() => import('@/pages/VideoSettings'));
const VideoApproval = React.lazy(() => import('@/pages/VideoApproval'));

// Lazy load main pages for better performance
const Dashboard = React.lazy(() => import('@/pages/Dashboard'));
const Projects = React.lazy(() => import('@/pages/Projects'));
const ProjectDetail = React.lazy(() => import('@/pages/ProjectDetail'));
const CreateProject = React.lazy(() => import('@/pages/CreateProject'));
const CreateVideoV2 = React.lazy(() => import('@/pages/CreateVideoV2'));
const Scheduling = React.lazy(() => import('@/pages/Scheduling'));
const Analytics = React.lazy(() => import('@/pages/Analytics'));
const Channels = React.lazy(() => import('@/pages/Channels'));
const Settings = React.lazy(() => import('@/pages/Settings'));
const Upgrade = React.lazy(() => import('@/pages/Upgrade'));
const Onboarding = React.lazy(() => import('@/pages/Onboarding'));
const QuickStartGuide = React.lazy(() => import('@/pages/QuickStartGuide'));
const NotFound = React.lazy(() => import('@/pages/NotFound'));


// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 1,
    },
  },
});

// Page Loading Component
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <LoadingSpinner size="lg" />
  </div>
);

// Auth Layout Component
const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {children}
    </div>
  );
};

// Main App Component
const App: React.FC = () => {
  const { checkAuthState, isAuthenticated, isLoading } = useAuthStore();
  const { theme, setCurrentTenant } = useAppStore();

  // Initialize auth state and theme on app load
  useEffect(() => {
    // Check authentication state
    checkAuthState();

    // Apply theme class to document
    document.documentElement.classList.toggle('dark', theme === 'dark');

    // Set default tenant theme (can be customized per user)
    setCurrentTenant('default', {
      primary: '#3b82f6',
      secondary: '#64748b', 
      accent: '#06b6d4',
      background: '#ffffff',
      surface: '#f8fafc',
    });
  }, [checkAuthState, theme, setCurrentTenant]);

  // Show loading spinner during initial auth check
  if (isLoading) {
    return <PageLoader />;
  }

return (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Auth Routes */}
            <Route path="/auth/*" element={
              <AuthLayout>
                <Routes>
                  <Route path="signin" element={<SignIn />} />
                  <Route path="signup" element={<SignUp />} />
                  <Route path="forgot-password" element={<ForgotPassword />} />
                  <Route path="*" element={<Navigate to="/auth/signin" replace />} />
                </Routes>
              </AuthLayout>
            } />

            {/* TOP-LEVEL ROUTES - BEFORE catch-all /* route */}
            
            {/* Onboarding Routes - Outside MainLayout to avoid conflicts */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <Onboarding />
                </Suspense>
              </ProtectedRoute>
            } />

            <Route path="/channels/:channelId/settings" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ChannelSettings />
                </Suspense>
              </ProtectedRoute>
            } />

            {/* Change #16: FIXED - Video Settings Route at TOP-LEVEL */}
            <Route path="/video-settings/:channelId" element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <VideoSettings />
                </Suspense>
              </ProtectedRoute>
            } />

            <Route path="/auth/youtube/success" element={<YouTubeAuthSuccess />} />

            {/* Root redirect */}
            <Route path="/" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/auth/signin" replace />
            } />

            {/* NESTED APP ROUTES - MUST BE LAST */}
            <Route path="/*" element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }>
              {/* Dashboard */}
              <Route path="dashboard" element={
                <Suspense fallback={<PageLoader />}>
                  <Dashboard />
                </Suspense>
              } />

              {/* Quick Start Guide */}
              <Route path="quick-start" element={
                <Suspense fallback={<PageLoader />}>
                  <QuickStartGuide />
                </Suspense>
              } />

              {/* Projects Routes */}
              <Route path="projects" element={
                <Suspense fallback={<PageLoader />}>
                  <Projects />
                </Suspense>
              } />
              <Route path="projects/new" element={
                <Suspense fallback={<PageLoader />}>
                  <CreateProject />
                </Suspense>
              } />
              <Route path="create-video" element={
                <Suspense fallback={<PageLoader />}>
                  <CreateVideoV2 />
                </Suspense>
              } />
              <Route path="projects/:projectId" element={
                <Suspense fallback={<PageLoader />}>
                  <ProjectDetail />
                </Suspense>
              } />
              <Route path="video-approval" element={
                <Suspense fallback={<PageLoader />}>
                  <VideoApproval />
                </Suspense>
              } />

              {/* Scheduling Routes - Pro Feature */}
              <Route path="scheduling" element={
                <ProtectedRoute requiredFeature="auto_scheduling">
                  <Suspense fallback={<PageLoader />}>
                    <Scheduling />
                  </Suspense>
                </ProtectedRoute>
              } />

              {/* Analytics Routes - Pro Feature */}
              <Route path="analytics" element={
                <ProtectedRoute requiredFeature="analytics">
                  <Suspense fallback={<PageLoader />}>
                    <Analytics />
                  </Suspense>
                </ProtectedRoute>
              } />

              {/* Channels Routes */}
              <Route path="channels" element={
                <Suspense fallback={<PageLoader />}>
                  <Channels />
                </Suspense>
              } />

              {/* Settings Routes */}
              <Route path="settings" element={
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              } />
              <Route path="settings/:tab" element={
                <Suspense fallback={<PageLoader />}>
                  <Settings />
                </Suspense>
              } />

              {/* Upgrade/Subscription Routes */}
              <Route path="upgrade" element={
                <Suspense fallback={<PageLoader />}>
                  <Upgrade />
                </Suspense>
              } />

              {/* 404 Not Found */}
              <Route path="*" element={
                <Suspense fallback={<PageLoader />}>
                  <NotFound />
                </Suspense>
              } />
            </Route>
          </Routes>
        </div>

        {/* Global Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: theme === 'dark' ? '#374151' : '#ffffff',
              color: theme === 'dark' ? '#ffffff' : '#374151',
              border: `1px solid ${theme === 'dark' ? '#4b5563' : '#e5e7eb'}`,
            },
            success: {
              iconTheme: {
                primary: '#22c55e',
                secondary: '#ffffff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#ffffff',
              },
            },
          }}
        />
      </Router>
    </ErrorBoundary>
  </QueryClientProvider>
);
};

export default App;